import { normalizePlu, parseMoney, parseQty, type Milli, type Paisa } from '@ims/core'
import type { JsonValue, ProductRow, RowOf } from '@ims/data'
import { cleanAttributes, parseTags, type AttributeField } from '../catalog.ts'
import { findUnit, unitForNew } from '../units.ts'
import {
  parseVariantText,
  readOptions,
  variantName,
  variantSku,
  type VariantValues,
} from '../variants.ts'
import { attrKey, fieldLabel, type MapKey, type Mapping } from './fields.ts'
import {
  CellError,
  cleanNumber,
  parseBool,
  parseDateCell,
  roundDecimal,
  toAsciiDigits,
} from './normalize.ts'

type UnitRow = RowOf<'units'>
type TaxRateRow = RowOf<'tax_rates'>
type CategoryRow = RowOf<'categories'>

export interface ImportOptions {
  /** Used when the file has no "price includes VAT" column (or the cell is empty). */
  pricesIncludeVat: boolean
  /** Used when the file has no VAT column (or the cell is empty). */
  defaultTaxRateId: string | null
  /** What to do with rows whose SKU (or barcode) already exists. */
  onExisting: 'skip' | 'update'
  /** Import at most this many new products (plan limit); the rest are skipped. */
  createLimit?: number | null
  /**
   * Choices for units the file uses that the business doesn't have, by
   * NewUnit.key. Missing = create it with the suggested decimals setting.
   */
  newUnits?: Record<string, { create: boolean; allowsDecimal: boolean }>
}

export interface ImportContext {
  products: ProductRow[]
  units: UnitRow[]
  taxRates: TaxRateRow[]
  categories: CategoryRow[]
  options: ImportOptions
  /** Products the plan still allows (max_products − current); null = unlimited. */
  productsRemaining: number | null
  /** Whether this user may add units (staff and up). Default true. */
  canCreateUnits?: boolean
  brands?: RowOf<'brands'>[]
  /** Attribute fields the business shows (medicine details, custom fields…). */
  attributeFields?: AttributeField[]
  /** Products with stock history (they can't become variant groups). */
  stockedProductIds?: ReadonlySet<string>
}

export interface ParsedProduct {
  sku: string
  skuGenerated: boolean
  name: string
  barcode: string | null
  categoryName: string | null
  categoryId: string | null
  unitId: string | null
  /** Set when the unit is one the import will create (NewUnit.key). */
  newUnitKey: string | null
  sellPricePaisa: Paisa
  costPricePaisa: Paisa | null
  taxRateId: string | null
  priceIncludesTax: boolean
  openingMilli: Milli | null
  minStockMilli: Milli | null
  description: string | null
  brandName: string | null
  brandId: string | null
  manufacturer: string | null
  mrpPaisa: Paisa | null
  wholesalePricePaisa: Paisa | null
  minPricePaisa: Paisa | null
  pluCode: string | null
  hsCode: string | null
  shelfLocation: string | null
  tags: string | null
  /** Opening stock batch (new products); batchNo is set whenever a date is. */
  batchNo: string | null
  mfgDate: string | null
  expiryDate: string | null
  /** Variant rows: the main product's SKU, and this variant's option values. */
  groupCode: string | null
  /** The main product when it exists already (it becomes a group if it isn't one). */
  group: ProductRow | null
  /** The main product's name when the import creates it. */
  groupName: string | null
  variantValues: VariantValues | null
  /** Attribute values from the file (only the mapped, non-empty ones). */
  attributes: Record<string, JsonValue> | null
}

export interface CellIssue {
  field: MapKey
  message: string
}

export interface FieldChange {
  field: string
  label: string
  from: string
  to: string
}

export interface PlannedRow {
  /** Index into the data rows. */
  index: number
  /** 1-based row number in the sheet, for messages and the error file. */
  rowNumber: number
  status: 'create' | 'update' | 'skip' | 'error'
  errors: CellIssue[]
  notes: string[]
  values?: ParsedProduct
  existing?: ProductRow
  /** Product columns to change on an update. */
  changes?: Partial<ProductRow>
  diff?: FieldChange[]
  /** A new product left out because of the plan limit (status 'skip'). */
  overLimit?: boolean
}

/** A unit the file uses that the business doesn't have yet. */
export interface NewUnit {
  key: string
  symbol: string
  name: string
  /** The suggestion, before the user's choice. */
  suggestedDecimal: boolean
  create: boolean
  allowsDecimal: boolean
  /** Spellings seen in the file ("pkt", "packet"). */
  spellings: string[]
  rowCount: number
}

export interface ImportPlan {
  rows: PlannedRow[]
  counts: { create: number; update: number; skip: number; error: number }
  /** New category names (as first written) that the import would create. */
  newCategories: string[]
  /** New brand names (as first written) that the import would create. */
  newBrands: string[]
  /** Units to create (ticked or not); rows only use the ticked ones. */
  newUnits: NewUnit[]
  /** How many new products are over the plan limit (0 when within it). */
  overLimitBy: number
}
import { skuFromName } from '../sku.ts'

export { skuFromName }

function lower(value: string): string {
  return value.trim().toLowerCase()
}

function money(raw: string): Paisa {
  const cleaned = cleanNumber(raw)
  if (cleaned === null) throw new CellError('empty')
  return parseMoney(roundDecimal(cleaned, 2))
}

function quantity(raw: string, allowsDecimal: boolean): Milli {
  const cleaned = cleanNumber(raw)
  if (cleaned === null) throw new CellError('empty')
  if (cleaned.startsWith('-')) throw new CellError('cannot be negative')
  const value = roundDecimal(cleaned, 3)
  if (!allowsDecimal && /\.\d*[1-9]/.test(value)) {
    throw new CellError('this unit does not allow decimals')
  }
  return parseQty(value, true)
}

/** "13", "13%", "0.13" (a percent-formatted cell read as a fraction) → basis points. */
function percentBp(raw: string): number {
  // The number inside labels like "VAT 13%" or "13 % vat".
  const found = /\d+(?:[.,]\d+)?/.exec(toAsciiDigits(raw))
  if (!found) throw new CellError(`“${raw}” is not a VAT rate`)
  const cleaned = cleanNumber(found[0])
  if (cleaned === null) throw new CellError('empty')
  let n = Number(cleaned)
  if (n > 0 && n < 1) n *= 100
  if (n < 0 || n > 100) throw new CellError(`${raw} is not a VAT rate`)
  return Math.round(n * 100)
}

function formatPaisa(p: Paisa | null): string {
  return p === null ? '—' : (p / 100).toFixed(2)
}

/** A cell as the value an attribute field stores ("Yes" → true, "12.5" → 12.5, a choice → its spelling). */
function attributeValue(field: AttributeField, raw: string): JsonValue {
  switch (field.type) {
    case 'number': {
      const cleaned = cleanNumber(raw)
      if (cleaned === null) throw new CellError('empty')
      return Number(cleaned)
    }
    case 'yesno': {
      const b = parseBool(raw)
      if (b === null) throw new CellError('empty')
      return b
    }
    case 'date':
      return parseDateCell(raw)
    case 'select': {
      const match = (field.options ?? []).find((o) => lower(o) === lower(raw))
      if (!match) {
        throw new CellError(`“${raw}” is not one of: ${(field.options ?? []).join(', ')}`)
      }
      return match
    }
    default:
      if (raw.length > 200) throw new CellError(`${field.label} is longer than 200 characters`)
      return raw
  }
}

const sameJson = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)

/**
 * Validates every data row against the mapping and the business's current
 * catalog, and decides what the import would do with it. Pure: call it again
 * after the user edits a cell or changes an option.
 */
export function buildPlan(
  dataRows: string[][],
  rowNumbers: number[],
  mapping: Mapping,
  ctx: ImportContext,
): ImportPlan {
  const { options } = ctx
  const unitOf = (raw: string) => findUnit(ctx.units, raw)
  const newUnits = new Map<string, NewUnit>()
  const canCreateUnits = ctx.canCreateUnits ?? true
  const activeRates = ctx.taxRates.filter((t) => t.is_active)
  const pcs = unitOf('pcs')
  const bySku = new Map(ctx.products.map((p) => [lower(p.sku), p]))
  const byBarcode = new Map<string, ProductRow | null>()
  for (const p of ctx.products) {
    if (!p.barcode) continue
    // Two products with one barcode: ambiguous, so don't match on it.
    byBarcode.set(p.barcode, byBarcode.has(p.barcode) ? null : p)
  }
  const categoriesByName = new Map(ctx.categories.map((c) => [lower(c.name), c]))
  const taxById = new Map(ctx.taxRates.map((t) => [t.id, t]))
  const unitById = new Map(ctx.units.map((u) => [u.id, u]))
  const catById = new Map(ctx.categories.map((c) => [c.id, c]))
  const brandsByName = new Map((ctx.brands ?? []).map((b) => [lower(b.name), b]))
  const brandById = new Map((ctx.brands ?? []).map((b) => [b.id, b]))
  const mappedAttributes = (ctx.attributeFields ?? []).filter(
    (f) => mapping[attrKey(f.key)] !== undefined,
  )

  const seenSku = new Map<string, number>()
  const newCategories = new Map<string, string>()
  const newBrands = new Map<string, string>()
  const rows: PlannedRow[] = []
  let creates = 0

  const cell = (row: string[], field: MapKey): string => {
    const col = mapping[field]
    return col === undefined ? '' : (row[col] ?? '').trim()
  }

  // Main products the import creates: named after the first row that has a name.
  const newGroupNames = new Map<string, string>()
  for (const row of dataRows) {
    const code = cell(row, 'group_code')
    const rowName = cell(row, 'name')
    if (code && rowName && !bySku.has(lower(code)) && !newGroupNames.has(lower(code)))
      newGroupNames.set(lower(code), rowName)
  }

  dataRows.forEach((row, index) => {
    const rowNumber = rowNumbers[index] ?? index + 1
    const mappedValues = Object.values(mapping)
      .filter((col): col is number => col !== undefined)
      .map((col) => (row[col] ?? '').trim())
    if (mappedValues.every((v) => v === '')) return // blank row

    const errors: CellIssue[] = []
    const notes: string[] = []
    const fail = (field: MapKey, message: string) => errors.push({ field, message })
    const attempt = <T>(field: MapKey, fn: () => T): T | null => {
      try {
        return fn()
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error)
        fail(field, message === 'empty' ? `${fieldLabel(field)} is required` : message)
        return null
      }
    }

    // Variant of a main product
    const groupCode = cell(row, 'group_code') || null
    let group: ProductRow | null = null
    let groupName: string | null = null
    let variantValues: VariantValues | null = null
    if (groupCode) {
      group = bySku.get(lower(groupCode)) ?? null
      groupName = group?.name ?? newGroupNames.get(lower(groupCode)) ?? null
      if (group?.kind === 'variant') fail('group_code', `${groupCode} is itself a variant`)
      else if (group && group.kind !== 'group' && ctx.stockedProductIds?.has(group.id))
        fail('group_code', `${groupCode} has stock history, so it can't hold variants`)
      else if (!groupName)
        fail('group_code', `No product ${groupCode}: import it first, or add a Name column`)
      variantValues = parseVariantText(
        cell(row, 'variant'),
        Object.keys(readOptions(group?.variant_options)),
      )
      if (Object.keys(variantValues).length === 0)
        fail('variant', 'Enter the variant, like “Size: M” or “M / Red”')
    }

    // Name (a variant is named after its main product when the file has no name)
    let name = cell(row, 'name')
    if (!name && groupName && variantValues && Object.keys(variantValues).length > 0) {
      name = variantName(groupName, variantValues, readOptions(group?.variant_options))
    }
    if (!name) fail('name', 'Name is required')
    else if (name.length > 200) fail('name', 'Name is longer than 200 characters')

    // Unit first: it decides whether quantities may have decimals.
    const unitRaw = cell(row, 'unit')
    let unit: UnitRow | null = pcs
    let newUnit: NewUnit | null = null
    if (unitRaw) {
      unit = unitOf(unitRaw)
      if (!unit && canCreateUnits) {
        const spec = unitForNew(unitRaw)
        const unitKey = spec.symbol.toLowerCase()
        newUnit = newUnits.get(unitKey) ?? null
        if (!newUnit) {
          const choice = options.newUnits?.[unitKey]
          newUnit = {
            key: unitKey,
            symbol: spec.symbol,
            name: spec.name,
            suggestedDecimal: spec.allowsDecimal,
            create: choice?.create ?? true,
            allowsDecimal: choice?.allowsDecimal ?? spec.allowsDecimal,
            spellings: [],
            rowCount: 0,
          }
          newUnits.set(unitKey, newUnit)
        }
        if (!newUnit.spellings.includes(unitRaw)) newUnit.spellings.push(unitRaw)
        newUnit.rowCount++
        if (!newUnit.create) {
          fail('unit', `Unit “${unitRaw}” is not being created. Tick it above or change the unit`)
        }
      } else if (!unit) {
        fail(
          'unit',
          `Unknown unit “${unitRaw}”. Use one of: ${ctx.units.map((u) => u.symbol).join(', ')}, or ask an admin to add it in Settings`,
        )
      }
    }
    const allowsDecimal = newUnit ? newUnit.allowsDecimal : (unit?.allows_decimal ?? false)

    // Prices. With no price column, the MRP is the selling price.
    const mrpRaw = cell(row, 'mrp')
    const sellField: MapKey = mapping.sell_price === undefined && mrpRaw ? 'mrp' : 'sell_price'
    const sellRaw = cell(row, sellField)
    const sell = sellRaw
      ? attempt(sellField, () => {
          const p = money(sellRaw)
          if (p < 0) throw new CellError('Selling price cannot be negative')
          return p
        })
      : null
    const costRaw = cell(row, 'cost_price')
    const cost = costRaw
      ? attempt('cost_price', () => {
          const p = money(costRaw)
          if (p < 0) throw new CellError('Cost price cannot be negative')
          return p
        })
      : null

    // VAT
    const vatRaw = cell(row, 'vat_rate')
    let taxRateId = options.defaultTaxRateId
    const namedRate = vatRaw ? activeRates.find((t) => lower(t.name) === lower(vatRaw)) : undefined
    if (namedRate) {
      taxRateId = namedRate.id
    } else if (vatRaw) {
      const exempt = /^(exempt|none|nil|no|zero|non ?vat|0%?)$/i.test(vatRaw)
      const bp = exempt ? 0 : attempt('vat_rate', () => percentBp(vatRaw))
      if (bp !== null) {
        const rate = activeRates.find((t) => t.rate_bp === bp)
        if (rate) taxRateId = rate.id
        else {
          fail(
            'vat_rate',
            `No ${bp / 100}% VAT rate. Use one of: ${activeRates.map((t) => `${t.rate_bp / 100}%`).join(', ')}, or ask an admin to add it in Settings → Tax rates`,
          )
        }
      }
    }
    const inclRaw = cell(row, 'price_includes_vat')
    const includes = inclRaw
      ? (attempt('price_includes_vat', () => parseBool(inclRaw)) ?? options.pricesIncludeVat)
      : options.pricesIncludeVat

    // Quantities
    const openingRaw = cell(row, 'opening_stock')
    const opening = openingRaw
      ? attempt('opening_stock', () => quantity(openingRaw, allowsDecimal))
      : null
    const minRaw = cell(row, 'min_stock')
    const minStock = minRaw ? attempt('min_stock', () => quantity(minRaw, allowsDecimal)) : null

    // Codes
    const barcode = cell(row, 'barcode').replace(/\s+/g, '') || null
    if (barcode && barcode.length > 64) fail('barcode', 'Barcode is too long')
    let sku = cell(row, 'sku')
    const skuGenerated = sku === '' && name !== ''
    if (skuGenerated)
      sku = groupCode && variantValues ? variantSku(groupCode, variantValues) : skuFromName(name)
    if (groupCode && lower(sku) === lower(groupCode))
      fail('sku', 'A variant needs its own SKU, not the main product’s')
    if (sku.length > 64) fail('sku', 'SKU is longer than 64 characters')

    // Match an existing product: by SKU, else by barcode.
    // A typed SKU wins; a code made from the name only matches after the barcode.
    let existing: ProductRow | undefined
    if (sku && !skuGenerated) existing = bySku.get(lower(sku))
    if (!existing && barcode) existing = byBarcode.get(barcode) ?? undefined
    if (!existing && skuGenerated) existing = bySku.get(lower(sku))

    // Duplicates inside the file.
    if (sku) {
      const key = lower(existing?.sku ?? sku)
      const first = seenSku.get(key)
      if (first !== undefined) {
        if (skuGenerated && !existing) {
          // Two different rows with the same name: number the generated code.
          let n = 2
          while (seenSku.has(lower(`${sku}-${n}`)) || bySku.has(lower(`${sku}-${n}`))) n++
          sku = `${sku}-${n}`
          seenSku.set(lower(sku), rowNumber)
        } else {
          fail('sku', `Same SKU as row ${first}`)
        }
      } else {
        seenSku.set(key, rowNumber)
      }
    }

    // Category
    const categoryName = cell(row, 'category') || null
    let categoryId: string | null = null
    if (categoryName) {
      const found = categoriesByName.get(lower(categoryName))
      if (found) categoryId = found.id
      else if (!newCategories.has(lower(categoryName)))
        newCategories.set(lower(categoryName), categoryName)
    }

    const description = cell(row, 'description') || null

    // Brand, catalog details and attribute fields
    const brandName = cell(row, 'brand') || null
    let brandId: string | null = null
    if (brandName) {
      const found = brandsByName.get(lower(brandName))
      if (found) brandId = found.id
      else if (!newBrands.has(lower(brandName))) newBrands.set(lower(brandName), brandName)
    }
    const manufacturer = cell(row, 'manufacturer') || null
    const mrp = mrpRaw
      ? attempt('mrp', () => {
          const p = money(mrpRaw)
          if (p < 0) throw new CellError('MRP cannot be negative')
          return p
        })
      : null
    const optionalPrice = (field: 'wholesale_price' | 'min_price', label: string) => {
      const raw = cell(row, field)
      return raw
        ? attempt(field, () => {
            const p = money(raw)
            if (p < 0) throw new CellError(`${label} cannot be negative`)
            return p
          })
        : null
    }
    const wholesale = optionalPrice('wholesale_price', 'Wholesale price')
    const minPrice = optionalPrice('min_price', 'Minimum price')
    if (minPrice !== null && sell !== null && minPrice > sell)
      fail('min_price', 'Minimum price is above the selling price')
    const pluRaw = toAsciiDigits(cell(row, 'plu_code'))
    const pluCode = pluRaw ? normalizePlu(pluRaw) : null
    if (pluRaw && !/^\d{1,8}$/.test(pluRaw)) fail('plu_code', 'The scale code is up to 8 digits')
    const hsCode = cell(row, 'hs_code') || null
    if (hsCode && hsCode.length > 20) fail('hs_code', 'HS code is longer than 20 characters')
    const shelfLocation = cell(row, 'shelf_location') || null
    if (shelfLocation && shelfLocation.length > 60) fail('shelf_location', 'Shelf is too long')
    const tags = parseTags(cell(row, 'tags')).join(', ') || null
    // Batch (opening stock of new products)
    const mfgRaw = cell(row, 'mfg_date')
    const mfgDate = mfgRaw ? attempt('mfg_date', () => parseDateCell(mfgRaw)) : null
    const expiryRaw = cell(row, 'expiry_date')
    const expiryDate = expiryRaw ? attempt('expiry_date', () => parseDateCell(expiryRaw)) : null
    if (mfgDate && expiryDate && expiryDate < mfgDate)
      fail('expiry_date', 'Expiry is before the manufacture date')
    let batchNo = cell(row, 'batch_no') || null
    if (batchNo && batchNo.length > 60) fail('batch_no', 'Batch no. is longer than 60 characters')
    // Dates without a batch number still need a batch: name it after the expiry.
    if (!batchNo && (expiryDate || mfgDate)) batchNo = `EXP-${expiryDate ?? `MFG-${mfgDate}`}`
    const attributeValues: Record<string, JsonValue> = {}
    for (const f of mappedAttributes) {
      const raw = cell(row, attrKey(f.key))
      if (!raw) continue
      const value = attempt(attrKey(f.key), () => attributeValue(f, raw))
      if (value !== null) attributeValues[f.key] = value
    }
    const attributes = cleanAttributes(attributeValues)

    if (errors.length > 0) {
      rows.push({ index, rowNumber, status: 'error', errors, notes })
      return
    }

    const values: ParsedProduct = {
      sku,
      skuGenerated,
      name,
      barcode,
      categoryName,
      categoryId,
      unitId: newUnit ? null : (unit?.id ?? null),
      newUnitKey: newUnit?.key ?? null,
      sellPricePaisa: sell ?? 0,
      costPricePaisa: cost,
      taxRateId,
      priceIncludesTax: includes,
      openingMilli: opening,
      minStockMilli: minStock,
      description,
      brandName,
      brandId,
      manufacturer,
      mrpPaisa: mrp,
      wholesalePricePaisa: wholesale,
      minPricePaisa: minPrice,
      pluCode,
      hsCode,
      shelfLocation,
      tags,
      batchNo,
      mfgDate,
      expiryDate,
      groupCode,
      group,
      groupName,
      variantValues,
      attributes,
    }

    if (!existing) {
      if (sell === null) notes.push('No selling price: set one before billing')
      if (options.createLimit != null && creates >= options.createLimit) {
        rows.push({
          index,
          rowNumber,
          status: 'skip',
          errors,
          notes: ['Over the plan limit'],
          values,
          overLimit: true,
        })
        return
      }
      creates++
      rows.push({ index, rowNumber, status: 'create', errors, notes, values })
      return
    }

    if (opening !== null && opening > 0) notes.push('Opening stock is only used for new products')
    else if (batchNo) notes.push('Batches are only used for new products (receive stock instead)')
    if (options.onExisting === 'skip') {
      notes.unshift('Already in your products')
      rows.push({ index, rowNumber, status: 'skip', errors, notes, values, existing })
      return
    }

    // Update: only fields present in the file, and only when they differ.
    const current: ProductRow = existing
    const changes: Partial<ProductRow> = {}
    const diff: FieldChange[] = []
    const change = <K extends keyof ProductRow>(
      key: K,
      label: string,
      next: ProductRow[K],
      show: (v: ProductRow[K]) => string,
    ) => {
      if (current[key] === next) return
      changes[key] = next
      diff.push({ field: key, label, from: show(current[key]), to: show(next) })
    }
    const text = (v: unknown) => (v == null || v === '' ? '—' : String(v))
    if (cell(row, 'name')) change('name', 'Name', name, text)
    if (mapping.barcode !== undefined && barcode) change('barcode', 'Barcode', barcode, text)
    if (sell !== null) {
      change('sell_price_paisa', 'Selling price', sell, formatPaisa)
    }
    if (mapping.cost_price !== undefined && cost !== null) {
      change('cost_price_paisa', 'Cost price', cost, formatPaisa)
    }
    if (mapping.vat_rate !== undefined && vatRaw) {
      change('tax_rate_id', 'VAT', taxRateId, (id) => (id ? (taxById.get(id)?.name ?? '—') : '—'))
    }
    if (mapping.price_includes_vat !== undefined && inclRaw) {
      change('price_includes_tax', 'Price includes VAT', includes, (v) => (v ? 'Yes' : 'No'))
    }
    if (mapping.unit !== undefined && unitRaw && !newUnit) {
      change('unit_id', 'Unit', values.unitId, (id) =>
        id ? (unitById.get(id)?.symbol ?? '—') : '—',
      )
    }
    if (mapping.category !== undefined && categoryName && categoryId) {
      change('category_id', 'Category', categoryId, (id) =>
        id ? (catById.get(id)?.name ?? '—') : '—',
      )
    }
    if (mapping.min_stock !== undefined && minStock !== null) {
      change('min_stock_milli', 'Low-stock alert', minStock, (v) => String(Number(v) / 1000))
    }
    if (mapping.description !== undefined && description) {
      change('description', 'Description', description, text)
    }
    if (brandName && brandId) {
      change('brand_id', 'Brand', brandId, (id) => (id ? (brandById.get(id)?.name ?? '—') : '—'))
    }
    if (manufacturer) change('manufacturer', 'Manufacturer', manufacturer, text)
    if (mrp !== null) change('mrp_paisa', 'MRP', mrp, formatPaisa)
    if (wholesale !== null)
      change('wholesale_price_paisa', 'Wholesale price', wholesale, formatPaisa)
    if (minPrice !== null) change('min_price_paisa', 'Minimum price', minPrice, formatPaisa)
    if (pluCode) change('plu_code', 'Scale code', pluCode, text)
    if (hsCode) change('hs_code', 'HS code', hsCode, text)
    if (shelfLocation) change('shelf_location', 'Shelf', shelfLocation, text)
    if (tags) change('tags', 'Tags', tags, text)
    if (attributes) {
      // Merge into what the product already has; values not in the file are kept.
      const before =
        current.attributes &&
        typeof current.attributes === 'object' &&
        !Array.isArray(current.attributes)
          ? current.attributes
          : {}
      const merged = { ...before, ...attributes }
      if (!sameJson(before, merged)) {
        changes.attributes = merged
        for (const [key, value] of Object.entries(attributes)) {
          if (sameJson(before[key], value)) continue
          const f = mappedAttributes.find((x) => x.key === key)
          diff.push({
            field: `attr:${key}`,
            label: f?.label ?? key,
            from: text(before[key]),
            to: text(value),
          })
        }
      }
    }
    if (brandName && !brandId) notes.push(`New brand “${brandName}” is set after it’s created`)
    if (categoryName && !categoryId)
      notes.push(`New category “${categoryName}” is set after it’s created`)
    if (newUnit) {
      diff.push({
        field: 'unit_id',
        label: 'Unit',
        from: current.unit_id ? (unitById.get(current.unit_id)?.symbol ?? '—') : '—',
        to: `${newUnit.symbol} (new)`,
      })
    }

    if (diff.length === 0 && !(categoryName && !categoryId) && !(brandName && !brandId)) {
      notes.unshift('No changes')
      rows.push({ index, rowNumber, status: 'skip', errors, notes, values, existing })
      return
    }
    rows.push({
      index,
      rowNumber,
      status: 'update',
      errors,
      notes,
      values,
      existing,
      changes,
      diff,
    })
  })

  const counts = { create: 0, update: 0, skip: 0, error: 0 }
  for (const r of rows) counts[r.status]++
  const overLimitBy =
    ctx.productsRemaining === null ? 0 : Math.max(0, counts.create - ctx.productsRemaining)

  return {
    rows,
    counts,
    newCategories: [...newCategories.values()],
    newBrands: [...newBrands.values()],
    newUnits: [...newUnits.values()],
    overLimitBy,
  }
}
